import { RfiItem } from '../../../operations-types'

export { RfiItem }

export type RfiRequest = {
  items: RfiItem[]
  note: string | null
}

export const LEGACY_RFI_ITEMS: readonly RfiItem[] = [
  RfiItem.Identity,
  RfiItem.ProofOfAddress,
  RfiItem.CertificateOfIncorporation,
]

export const RFI_DOCUMENT_ITEMS: readonly RfiItem[] = [
  RfiItem.ProofOfAddress,
  RfiItem.CertificateOfIncorporation,
]

export const RFI_ITEM_COPY: Record<RfiItem, { label: string; reason: string }> =
  {
    [RfiItem.Identity]: {
      label: 'ID verification',
      reason: "Confirms you're the person who opened this account.",
    },
    [RfiItem.ProofOfAddress]: {
      label: 'Proof of business address',
      reason: 'Confirms a registered business at the address you gave us.',
    },
    [RfiItem.CertificateOfIncorporation]: {
      label: 'Certificate of Incorporation',
      reason: 'Confirms a registered business at the address you gave us.',
    },
    [RfiItem.PrimaryBankAccount]: {
      label: 'Primary business bank account',
      reason:
        "Your primary operating account is the strongest proof of business we can review, and it's what lets us move your payments at the speeds Nickel is built for. We'll keep it on file alongside the account you already provided.",
    },
  }

const RFI_ITEM_VALUES = new Set<string>(Object.values(RfiItem))

export function rfiRequestFromOnboardingInfo(
  onboardingInfo: Record<string, unknown>,
): RfiRequest {
  const raw = onboardingInfo.rfiRequest
  if (!raw || typeof raw !== 'object') {
    return { items: [...LEGACY_RFI_ITEMS], note: null }
  }
  const { items, note } = raw as { items?: unknown; note?: unknown }
  const known = Array.isArray(items)
    ? (items.filter(
        (item): item is RfiItem =>
          typeof item === 'string' && RFI_ITEM_VALUES.has(item),
      ) as RfiItem[])
    : []
  return {
    items: known.length > 0 ? known : [...LEGACY_RFI_ITEMS],
    note: typeof note === 'string' && note.trim() ? note : null,
  }
}

/**
 * One request round: what was asked and when it was opened. A later round
 * asking for the same items is a different request (the API only accepts a
 * bank linked since the round opened), so a draft never outlives its round.
 */
export function rfiRequestKey(
  request: RfiRequest,
  onboardingInfo: Record<string, unknown>,
): string {
  const raw = onboardingInfo.rfiRequest
  const requestedAt =
    raw && typeof raw === 'object' && 'requestedAt' in raw
      ? raw.requestedAt
      : null
  return JSON.stringify([
    request.items,
    request.note,
    typeof requestedAt === 'string' ? requestedAt : null,
  ])
}

export type RfiStep =
  | 'get-started'
  | 'additional-info'
  | 'identity'
  | 'documents'
  | 'bank-account'
  | 'submitted'

export const RFI_STEP_LABELS: Record<RfiStep, string> = {
  'get-started': 'Get started',
  'additional-info': 'Additional information',
  identity: 'Verify identity',
  documents: 'Documents',
  'bank-account': 'Bank account',
  submitted: 'Submitted',
}

export function rfiStepsFor(
  items: readonly RfiItem[],
  note: string | null = null,
): RfiStep[] {
  const steps: RfiStep[] = ['get-started']
  if (note) steps.push('additional-info')
  if (items.includes(RfiItem.Identity)) steps.push('identity')
  if (items.some((item) => RFI_DOCUMENT_ITEMS.includes(item)))
    steps.push('documents')
  if (items.includes(RfiItem.PrimaryBankAccount)) steps.push('bank-account')
  return steps
}

export function getNextStep(
  current: RfiStep,
  items: readonly RfiItem[],
  note: string | null = null,
) {
  const steps = rfiStepsFor(items, note)
  const idx = steps.indexOf(current)
  if (idx >= 0 && idx < steps.length - 1) return steps[idx + 1]
  return current
}

export function getPreviousStep(
  current: RfiStep,
  items: readonly RfiItem[],
  note: string | null = null,
) {
  const steps = rfiStepsFor(items, note)
  const idx = steps.indexOf(current)
  if (idx > 0) return steps[idx - 1]
  return current
}
